import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme, Snackbar, Alert } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion } from 'framer-motion';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const FAQ = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (location.state?.message) {
      setSnackbar({
        open: true,
        message: location.state.message,
        severity: location.state.severity || 'success',
      });
      // Clear the state to prevent the message from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);
  // Reorganized FAQs with categories
  const faqCategories = [
    {
      title: "Donation Process",
      faqs: [
        {
          question: 'How does the car donation process work?',
          answer: 'The process is simple and straightforward: 1) Fill out our online donation form or call us with your vehicle information, 2) We schedule a convenient pickup time, 3) Your vehicle is picked up for free by our professional towing service, 4) After the vehicle is sold, you receive tax deduction documentation. We handle all the paperwork and details for you!',
        },
        {
          question: 'What areas do you serve?',
          answer: 'We serve most major metropolitan areas across the United States. Our network of partners allows us to pick up vehicles nationwide. Contact us with your specific location for confirmation.',
        },
        {
          question: 'How long will it take to have my car donation picked up?',
          answer: 'In most areas, we can schedule a pickup within 24-48 hours of receiving your donation information. The exact timing depends on your location and schedule preferences.',
        },
        {
          question: 'Do I have to be home when my donated car is picked up?',
          answer: 'No, you don\'t have to be present for the pickup as long as the vehicle is accessible and all required paperwork has been completed beforehand. We can arrange the pickup details to suit your schedule.',
        },
        {
          question: 'If I donate my car, how does it get picked up?',
          answer: 'We work with professional, licensed towing companies who will pick up your vehicle at a scheduled time. They will provide all necessary equipment and ensure safe transport of your donated vehicle.',
        },
      ],
    },
    {
      title: "Vehicle Requirements & Documentation",
      faqs: [
        {
          question: 'When donating a car, does it have to be a running car?',
          answer: 'No, we accept both running and non-running vehicles. While operational vehicles may have higher value, we welcome all vehicle donations regardless of their condition.',
        },
        {
          question: 'What documents do you need from me if I donate my car?',
          answer: 'The main document required is a clear vehicle title in your name. Additional documentation may include a valid ID and signed donation forms that we provide. We\'ll guide you through any paperwork needed.',
        },
        {
          question: 'How is the value of my car donation determined?',
          answer: 'IRS standards allows the donor to determine the fair market value up to $500. If your donated vehicle is sold for over $500, the IRS allows you to claim the sale price as your deduction. With over 25 years in the automotive remarketing industry VFYV strives to maximize your tax deduction and the proceeds of your donated vehicle to benefit our Veteran Community.',
        },
        {
          question: 'Is there a charge for towing?',
          answer: 'No, there is absolutely no charge for towing. We provide free towing services for all vehicle donations, regardless of the vehicle\'s location or condition.',
        },
      ],
    },
    {
      title: "Other Ways to Help",
      faqs: [
        {
          question: 'Do you accept other donations besides vehicles?',
          answer: 'Yes, while vehicles are our primary focus, we also welcome other forms of support. This can include monetary donations, volunteer opportunities, and other resources that help support our veterans.',
        },
        {
          question: 'I cannot donate my car but would like to help support our veterans with a monetary gift.',
          answer: 'We greatly appreciate monetary donations! These contributions help us provide essential services to veterans. You can make a secure donation through our website or contact us for other giving options.',
        },
      ],
    },
  ];

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          pt: { xs: '100px', sm: '120px', md: '140px' },
          pb: { xs: '40px', sm: '60px', md: '80px' },
          px: { xs: 2, sm: 4, md: 0 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 50%)',
          }
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                textAlign: 'center',
                textShadow: '2px 4px 8px rgba(0,0,0,0.2)',
                fontFamily: "'Inter', sans-serif",
                background: 'linear-gradient(to right, #ffffff, #f0f0f0)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                position: 'relative',
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography 
              variant="h5"
              sx={{ 
                textAlign: 'center',
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.9,
                mb: 4,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              Find answers to common questions about our vehicle donation process
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* FAQ Sections */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        {faqCategories.map((category, categoryIndex) => (
          <motion.div
            key={category.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                mt: categoryIndex > 0 ? 6 : 0,
                fontWeight: 700,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                fontFamily: "'Inter', sans-serif",
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 32 }} />
              {category.title}
            </Typography>

            {category.faqs.map((faq, index) => (
              <Accordion
                key={index}
                sx={{
                  mb: 2,
                  borderRadius: '8px !important',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    '& .MuiAccordionSummary-content': {
                      fontFamily: "'Inter', sans-serif",
                    }
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </motion.div>
        ))}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={10000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FAQ;
